import { errorMessage, type ApiClient, type ApiErrorBody } from "@planner/core";
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Floating } from "../components/Floating.js";
import { PersonPhoto } from "../components/PersonPhoto.js";
import { useLocale } from "../i18n/provider.js";
import { platform } from "../platform/index.js";
import { useToast } from "./Toast.js";

/**
 * **بطاقةُ الشخص — مكوّنٌ واحد في النظام كلّه** (§٧).
 *
 * اعتُمدت في ٢٠٢٦-٠٩-٢١ ولم تُبنَ: فالوجهُ كان صورةً ونقطةً لا أكثر. وأوّلُ من لاحظ غيابَها
 * شادي نفسُه وهو يجرّب النظام بعين موظف. **وتُركَّب على الوجه، فتصل كلَّ شاشةٍ فيها وجه.**
 *
 * **وخارج هذا المزوّد لا بطاقة:** فالبورتالُ لا يركّبه، «وظهورُها هناك تسريبُ هوية».
 */

type CardApi = {
  readonly hoverIn: (userId: string, anchor: HTMLElement) => void;
  readonly hoverOut: () => void;
  readonly tap: (userId: string, anchor: HTMLElement) => void;
};

const PersonCardContext = createContext<CardApi | null>(null);

export function usePersonCard(): CardApi | null {
  return use(PersonCardContext);
}

type Card = {
  readonly id: string;
  readonly name: string;
  readonly jobTitle: string | null;
  readonly status: "ACTIVE" | "INACTIVE";
  readonly photoUpdatedAt: string | null;
  readonly role: { readonly nameAr: string; readonly nameEn: string } | null;
  readonly departments: readonly { readonly nameAr: string; readonly nameEn: string }[];
  /** **من السيرفر، بقاعدة من يجوز له**: `null` لمن لا يجوز له، فلا تُقال عنه حالةٌ لا نعرفها */
  readonly presence: "online" | "away" | "offline" | null;
};

const OPEN_AFTER_MS = 300;
const CLOSE_AFTER_MS = 200;

export function PersonCardProvider({
  client,
  meId,
  canMessage,
  onOpenChat,
  children,
}: {
  readonly client: ApiClient;
  readonly meId: string;
  /** **وزرُّ المراسلة لمن يقبله السيرفر منه** — وبعين الموظف يظهر كما يظهر له */
  readonly canMessage: boolean;
  readonly onOpenChat: (channelId: string) => void;
  readonly children: ReactNode;
}): ReactNode {
  const [open, setOpen] = useState<{
    readonly userId: string;
    readonly anchor: HTMLElement;
  } | null>(null);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = useCallback(() => {
    if (openTimer.current !== null) clearTimeout(openTimer.current);
    if (closeTimer.current !== null) clearTimeout(closeTimer.current);
    openTimer.current = null;
    closeTimer.current = null;
  }, []);

  const api = useMemo<CardApi>(
    () => ({
      hoverIn: (userId, anchor) => {
        clearTimers();
        openTimer.current = setTimeout(() => {
          setOpen({ userId, anchor });
        }, OPEN_AFTER_MS);
      },
      hoverOut: () => {
        clearTimers();
        closeTimer.current = setTimeout(() => {
          setOpen(null);
        }, CLOSE_AFTER_MS);
      },
      tap: (userId, anchor) => {
        clearTimers();
        setOpen((now) => (now?.userId === userId ? null : { userId, anchor }));
      },
    }),
    [clearTimers],
  );

  useEffect(() => clearTimers, [clearTimers]);

  return (
    <PersonCardContext value={api}>
      {children}
      {open === null ? null : (
        /** **ولا بطاقةَ داخل بطاقة** — فوجهُ البطاقة لا يفتح نفسَه */
        <PersonCardContext value={null}>
          <CardPanel
            key={open.userId}
            client={client}
            userId={open.userId}
            anchor={open.anchor}
            meId={meId}
            canMessage={canMessage}
            onStay={clearTimers}
            onLeave={api.hoverOut}
            onClose={() => {
              setOpen(null);
            }}
            onOpenChat={(channelId) => {
              setOpen(null);
              onOpenChat(channelId);
            }}
          />
        </PersonCardContext>
      )}
    </PersonCardContext>
  );
}

function CardPanel({
  client,
  userId,
  anchor,
  meId,
  canMessage,
  onStay,
  onLeave,
  onClose,
  onOpenChat,
}: {
  readonly client: ApiClient;
  readonly userId: string;
  readonly anchor: HTMLElement;
  readonly meId: string;
  readonly canMessage: boolean;
  readonly onStay: () => void;
  readonly onLeave: () => void;
  readonly onClose: () => void;
  readonly onOpenChat: (channelId: string) => void;
}): ReactNode {
  const { t, locale } = useLocale();
  const toast = useToast();
  const [card, setCard] = useState<Card | null>(null);
  const [error, setError] = useState<ApiErrorBody | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    void (async () => {
      const result = await client.request<Card>(`/people/${userId}/card`, {
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      if (result.ok) setCard(result.data);
      else setError(result.error);
    })();
    return () => {
      controller.abort();
    };
  }, [client, userId]);

  const name = (row: { readonly nameAr: string; readonly nameEn: string }): string =>
    locale === "ar" ? row.nameAr : row.nameEn;

  async function message(): Promise<void> {
    setBusy(true);
    const result = await client.request<{ readonly id: string }>("/chat/direct", {
      method: "POST",
      idempotencyKey: platform.newId(),
      body: { userId },
    });
    setBusy(false);
    if (result.ok) onOpenChat(result.data.id);
    else toast.failed(result.error);
  }

  return (
    <Floating
      anchor={anchor}
      label={t("person.card")}
      className="person-card"
      onPointerEnter={onStay}
      onPointerLeave={onLeave}
    >
      <button
        type="button"
        className="sr-only"
        data-close
        aria-label={t("common.close")}
        onClick={onClose}
      />
      {error !== null ? (
        <p className="field-error">
          {errorMessage(locale, error)} <span className="codes">{error.code}</span>
        </p>
      ) : card === null ? (
        <div className="person-card-wait" aria-busy="true" />
      ) : (
        <>
          <div className="person-card-head">
            <PersonPhoto userId={card.id} name={card.name} stamp={card.photoUpdatedAt} size="lg" />
            <div>
              <b>{card.name}</b>
              {card.jobTitle ? <span>{card.jobTitle}</span> : null}
            </div>
          </div>
          <dl className="person-card-facts">
            {card.role ? (
              <div>
                <dd>{name(card.role)}</dd>
              </div>
            ) : null}
            {card.departments.length > 0 ? (
              <div>
                <dd>{card.departments.map(name).join(" · ")}</dd>
              </div>
            ) : null}
          </dl>
          {card.status !== "ACTIVE" ? (
            <p className="person-card-state">{t("person.stopped")}</p>
          ) : card.presence !== null ? (
            <p className={`person-card-state is-${card.presence}`}>
              <i aria-hidden="true" />
              {t(
                card.presence === "online"
                  ? "person.online"
                  : card.presence === "away"
                    ? "person.away"
                    : "person.offline",
              )}
            </p>
          ) : null}
          {canMessage && card.id !== meId && card.status === "ACTIVE" ? (
            <button
              type="button"
              className="btn sm"
              disabled={busy}
              onClick={() => {
                void message();
              }}
            >
              {t("person.message")}
            </button>
          ) : null}
        </>
      )}
    </Floating>
  );
}
